import { ethers } from 'ethers';
import BigNumber from 'bignumber.js';

// Format token amount with proper decimals
export const formatTokenAmount = (amount, decimals = 18, precision = 4) => {
  if (!amount) return '0';
  
  try {
    const bn = new BigNumber(amount);
    if (bn.isZero()) return '0';
    
    const formatted = bn.dividedBy(new BigNumber(10).pow(decimals));
    
    if (formatted.isLessThan(0.0001)) {
      return '< 0.0001';
    }
    
    return formatted.toFixed(precision, BigNumber.ROUND_DOWN);
  } catch (error) {
    console.error('Error formatting token amount:', error);
    return '0';
  }
};

// Parse token amount to wei/smallest unit
export const parseTokenAmount = (amount, decimals = 18) => {
  if (!amount || amount === '0') return '0';
  
  try {
    const bn = new BigNumber(amount);
    return bn.multipliedBy(new BigNumber(10).pow(decimals)).toFixed(0);
  } catch (error) {
    console.error('Error parsing token amount:', error);
    return '0';
  }
};

// Format address for display
export const formatAddress = (address, startLength = 6, endLength = 4) => {
  if (!address) return '';
  if (address.length < startLength + endLength) return address;
  
  return `${address.substring(0, startLength)}...${address.substring(
    address.length - endLength
  )}`;
};

// Calculate slippage amount
export const calculateSlippage = (amount, slippagePercent) => {
  if (!amount || !slippagePercent) return amount;
  
  try {
    const bn = new BigNumber(amount);
    const slippageBn = new BigNumber(slippagePercent).dividedBy(100);
    const slippageAmount = bn.multipliedBy(slippageBn);
    
    return bn.minus(slippageAmount).toFixed(0);
  } catch (error) {
    console.error('Error calculating slippage:', error);
    return amount;
  }
};

// Calculate fee amount (1%)
export const calculateFeeAmount = (amount, feePercent = 1) => {
  if (!amount) return '0';
  
  try {
    const bn = new BigNumber(amount);
    const feeBn = new BigNumber(feePercent).dividedBy(100);
    
    return bn.multipliedBy(feeBn).toFixed(0);
  } catch (error) {
    console.error('Error calculating fee amount:', error);
    return '0';
  }
};

// Calculate amount after fee
export const calculateAmountAfterFee = (amount, feePercent = 1) => {
  if (!amount) return '0';
  
  try {
    const bn = new BigNumber(amount);
    const feeBn = new BigNumber(feePercent).dividedBy(100);
    const feeAmount = bn.multipliedBy(feeBn);
    
    return bn.minus(feeAmount).toFixed(0);
  } catch (error) {
    console.error('Error calculating amount after fee:', error);
    return amount;
  }
};

// Validate ethereum address
export const isValidAddress = (address) => {
  try {
    return ethers.isAddress(address);
  } catch {
    return false;
  }
};

// Check if amount is valid
export const isValidAmount = (amount) => {
  if (!amount || amount === '0') return false;
  
  try {
    const bn = new BigNumber(amount);
    return bn.isPositive() && bn.isFinite();
  } catch {
    return false;
  }
};

// Format USD value
export const formatUSD = (amount, precision = 2) => {
  if (!amount) return '$0.00';
  
  try {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(num);
  } catch {
    return '$0.00';
  }
};

// Format percentage
export const formatPercentage = (value, precision = 2) => {
  if (!value) return '0%';
  
  try {
    const num = parseFloat(value);
    return `${num.toFixed(precision)}%`;
  } catch {
    return '0%';
  }
};

// Debounce function for input handling
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Get transaction URL for block explorer
export const getTransactionUrl = (txHash, chainId = 250) => {
  const explorers = {
    250: 'https://ftmscout.com/tx/',
    4002: 'https://testnet.ftmscan.com/tx/',
  };
  
  return `${explorers[chainId] || explorers[250]}${txHash}`;
};

// Convert BigNumber to display string
export const bigNumberToString = (bn, decimals = 18, precision = 4) => {
  if (!bn) return '0';
  
  try {
    return formatTokenAmount(bn.toString(), decimals, precision);
  } catch {
    return '0';
  }
};